import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaArrowLeft, FaSpinner, FaPlus, FaTrash, FaCheck, FaUpload, FaTimes,
  FaUserPlus, FaLink, FaFileAlt, FaTruck, FaUsers, FaHistory, FaCalendarAlt
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import ProjectGantt from '../components/ProjectGantt';
import ConfirmDialog from '../components/ConfirmDialog';
import { API_URL } from '../config';

// ============================================
// PROJECT DETAIL — drag Gantt schedule, files, links, people, activity
// ============================================
const token = () => localStorage.getItem('token');
const authHeaders = () => (token() ? { Authorization: `Bearer ${token()}` } : {});
const apiFetch = (path, opts = {}) => fetch(`${API_URL}${path}`, {
  ...opts,
  headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) }
});
const fmtDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const STATUS = {
  not_started: { label: 'Not started', color: '#94A3B8' },
  in_progress: { label: 'In progress', color: '#2563EB' },
  done: { label: 'Done', color: '#00A86B' },
  blocked: { label: 'Blocked', color: '#EF4444' }
};
const ITEM_TYPES = [
  { k: 'checklist', label: 'Checklist' },
  { k: 'upload', label: 'Upload' },
  { k: 'feedback', label: 'Feedback' }
];

function ProjectDetailPage({ onLogout }) {
  const { id } = useParams();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isStaff = ['admin', 'super_admin'].includes(user.role);
  const isAdmin = ['admin', 'super_admin'].includes(user.role);
  // Staff, or a project manager member, may change who has access.
  const myMemberRow = (m) => m.user_id && m.user_id === user.id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('schedule');
  const [taskPanel, setTaskPanel] = useState(null);
  const [users, setUsers] = useState([]);
  const [uploadingFor, setUploadingFor] = useState(null);

  // modals
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskForm, setTaskForm] = useState({ name: '', phase_id: '', start_date: '', end_date: '', status: 'not_started', progress: 0, description: '' });
  const [showPhaseModal, setShowPhaseModal] = useState(false);
  const [phaseName, setPhaseName] = useState('');
  const [showItemModal, setShowItemModal] = useState(false);
  // Branded in-app confirm — never window.confirm() (that shows the OS black box).
  const [confirmState, setConfirmState] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', start_date: '', end_date: '', description: '', location: '' });
  const [itemForm, setItemForm] = useState({ type: 'checklist', title: '', description: '', required: false, due_date: '' });
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [personForm, setPersonForm] = useState({ name: '', email: '', role_label: '' });
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkType, setLinkType] = useState('quote');
  const [linkQuery, setLinkQuery] = useState('');
  const [linkOptions, setLinkOptions] = useState([]);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [memberMode, setMemberMode] = useState('company'); // company | person | invite
  const [memberOptions, setMemberOptions] = useState({ companies: [], people: [] });
  const [companyOptions, setCompanyOptions] = useState([]);
  const [dateDraft, setDateDraft] = useState({ start: '', end: '' });
  const [inviteLink, setInviteLink] = useState(null);
  const [memberForm, setMemberForm] = useState({
    member_type: 'company', company_type: '', company_id: '', user_id: '',
    name: '', email: '', role_label: '', member_role: 'editor',
    access_type: 'permanent', level: 'full', expires_at: ''
  });

  // 📅 Reschedule — move the project (or one phase) and everything under it. Completed
  // tasks are left alone by default, so finished work keeps its actual dates.
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [shiftForm, setShiftForm] = useState({
    scope: 'project', phase_id: '', mode: 'days', days: 7, new_start: '', skip_completed: true
  });
  const [shiftPreview, setShiftPreview] = useState(null);
  const [shiftBusy, setShiftBusy] = useState(false);

  // Preview and apply call the SAME endpoint (preview:true just does not write), so the
  // old -> new list you approve is exactly what gets saved.
  const runShift = async (preview) => {
    setShiftBusy(true);
    try {
      const body = {
        scope: shiftForm.scope,
        phase_id: shiftForm.scope === 'phase' ? shiftForm.phase_id : undefined,
        mode: shiftForm.mode,
        days: Number(shiftForm.days) || 0,
        new_start: shiftForm.mode === 'start' ? shiftForm.new_start : undefined,
        skip_completed: !!shiftForm.skip_completed,
        preview
      };
      const d = await apiFetch(`/projects/${id}/shift`, { method: 'POST', body: JSON.stringify(body) }).then((r) => r.json());
      if (!d.success) throw new Error(d.error || 'Could not work out the move');
      if (preview) {
        setShiftPreview(d);
      } else {
        const c = d.counts || {};
        toast.success(`Rescheduled: ${c.phases || 0} phase(s) and ${c.tasks || 0} task(s) moved`
          + (d.skipped_completed_tasks ? `, ${d.skipped_completed_tasks} completed task(s) kept` : ''));
        setShowRescheduleModal(false);
        setShiftPreview(null);
        load();
      }
    } catch (e) {
      toast.error(e.message || 'Could not reschedule');
    } finally {
      setShiftBusy(false);
    }
  };

  const load = async () => {
    try {
      const res = await apiFetch(`/projects/${id}`);
      const d = await res.json();
      if (d.success) setData(d);
      else toast.error(d.error || 'Failed to load project');
    } catch (e) {
      console.error(e);
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (!isStaff) return;
    fetch(`${API_URL}/users`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => { if (d.success) setUsers(d.users || []); })
      .catch(() => {});
  }, [isStaff]);

  const canEditTask = (t) => isStaff || t?.created_by === user.id;
  const project = data?.project;
  const phases = data?.phases || [];
  const tasks = data?.tasks || [];

  // Keep the OPEN task panel in step with fresh data. The panel is opened from the
  // chart with a snapshot of the task, so without this a requirement/upload/check you
  // just added never appears until you close and re-open the task.
  useEffect(() => {
    if (!taskPanel) return;
    const fresh = (data?.tasks || []).find((t) => t.id === taskPanel.id);
    if (!fresh) return;
    setTaskPanel((p) => (p && p.id === fresh.id ? { ...p, ...fresh } : p));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const stats = useMemo(() => {
    const by = { not_started: 0, in_progress: 0, done: 0, blocked: 0 };
    tasks.forEach((t) => { by[t.status] = (by[t.status] || 0) + 1; });
    const items = tasks.flatMap((t) => t.items || []);
    const uploads = items.filter((i) => i.type === 'upload');
    const pendingUploads = uploads.filter((i) => !(i.checks || []).length).length;
    const doneItems = items.filter((i) => (i.checks || []).some((c) => c.status === 'done')).length;
    const overdue = tasks.filter((t) => t.end_date && t.status !== 'done' && t.end_date < new Date().toISOString().slice(0, 10)).length;
    return {
      total: tasks.length, ...by, items: items.length, doneItems,
      uploads: uploads.length, pendingUploads, overdue,
      pct: tasks.length ? Math.round((by.done / tasks.length) * 100) : 0
    };
  }, [tasks]);

  const post = async (path, body, method = 'POST') => {
    const res = await apiFetch(path, { method, body: JSON.stringify(body) });
    const d = await res.json();
    if (!d.success) throw new Error(d.error || 'Request failed');
    return d;
  };

  const reschedule = async (task, start_date, end_date) => {
    try {
      await post(`/projects/tasks/${task.id}`, { start_date, end_date }, 'PUT');
      toast.success(`📅 ${task.name} → ${fmtDate(end_date)}`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const addPhase = async () => {
    if (!phaseName.trim()) return;
    try {
      await post(`/projects/${id}/phases`, { name: phaseName.trim(), sort_order: phases.length });
      setPhaseName(''); setShowPhaseModal(false); load();
    } catch (e) { toast.error(e.message); }
  };

  const addTask = async (e) => {
    e.preventDefault();
    try {
      await post(`/projects/${id}/tasks`, taskForm);
      setShowTaskModal(false);
      setTaskForm({ name: '', phase_id: '', start_date: '', end_date: '', status: 'not_started', progress: 0, description: '' });
      load();
    } catch (err) { toast.error(err.message); }
  };

  const deleteTask = (task) => {
    setConfirmState({
      title: 'Delete this task?',
      message: `“${task.name}” will be removed from the schedule.\n\nThis cannot be undone.`,
      confirmLabel: 'Delete task',
      danger: true,
      onConfirm: async () => {
        try {
          const d = await apiFetch(`/projects/tasks/${task.id}`, { method: 'DELETE' }).then((r) => r.json());
          if (!d.success) throw new Error(d.error);
          setTaskPanel(null); load();
        } catch (e) { toast.error(e.message); }
      },
    });
  };

  // ── phases: delete + drag-to-reorder ──────────────────────────────────────
  const deletePhase = (phase) => {
    const count = (phase.tasks || []).length;
    const warn = count
      ? `“${phase.name}” still holds ${count} task${count === 1 ? '' : 's'}.\n\nDeleting the phase cannot be undone.`
      : `Deleting “${phase.name}” cannot be undone.`;
    setConfirmState({
      title: 'Delete this phase?',
      message: warn,
      confirmLabel: 'Delete phase',
      danger: true,
      onConfirm: async () => {
        try {
          const d = await apiFetch(`/projects/phases/${phase.id}`, { method: 'DELETE' }).then((r) => r.json());
          if (!d.success) throw new Error(d.error || 'Could not delete the phase');
          load();
        } catch (e) { toast.error(e.message); }
      },
    });
  };

  // Drop a phase row onto another phase → rewrite sort_order so the new order sticks,
  // which also lets you slot a new phase in between two existing ones.
  const movePhase = async (dragId, targetId) => {
    if (!dragId || !targetId || dragId === targetId) return;
    const order = phases.map((p) => p.id).filter((x) => x !== dragId);
    const at = order.indexOf(targetId);
    if (at === -1) return;
    order.splice(at, 0, dragId); // lands ABOVE the phase you dropped it onto
    try {
      for (let i = 0; i < order.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await post(`/projects/phases/${order[i]}`, { sort_order: i }, 'PUT');
      }
      load();
    } catch (e) { toast.error(e.message); }
  };

  // Deleting a project file removes it from BOTH the Project Files list and the
  // requirement it was uploaded against (it's one row) — so always ask first.
  const deleteFile = (file) => {
    setConfirmState({
      title: 'Delete this file?',
      message: `“${file.file_name || file.caption || 'This file'}” will be removed from the project files and from the requirement it sits on.\n\nThis cannot be undone.`,
      confirmLabel: 'Delete file',
      danger: true,
      onConfirm: async () => {
        try {
          const d = await apiFetch(`/projects/files/${file.id}`, { method: 'DELETE' }).then((r) => r.json());
          if (!d.success) throw new Error(d.error || 'Could not delete the file');
          load();
        } catch (e) { toast.error(e.message); }
      },
    });
  };

  // ── project: simple edit + owner-only delete ──────────────────────────────
  // Who may change/delete this project? Staff, or the client who OWNS it
  // (clients.user_id === the logged-in user). The backend enforces the same rule.
  const isProjectOwner = !!(data?.project?.client?.user_id && data.project.client.user_id === user.id);
  const canManageProject = isStaff || isProjectOwner
    || (user?.role === 'client' && !data?.project?.client?.user_id);

  const openEditProject = () => {
    setEditForm({
      name: project?.name || '',
      start_date: (project?.start_date || '').slice(0, 10),
      end_date: (project?.end_date || '').slice(0, 10),
      description: project?.description || '',
      location: project?.location || '',
    });
    setShowEditModal(true);
  };

  const saveProject = async (e) => {
    e.preventDefault();
    try {
      await post(`/projects/${id}`, editForm, 'PUT');
      setShowEditModal(false);
      toast.success('Project updated');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const deleteProject = () => {
    setConfirmState({
      title: 'Delete this project?',
      message: `“${project?.name || 'This project'}” and everything inside it — phases, tasks, requirements, files and links — will be removed.\n\nThis cannot be undone.`,
      confirmLabel: 'Delete project',
      danger: true,
      onConfirm: async () => {
        try {
          const d = await apiFetch(`/projects/${id}`, { method: 'DELETE' }).then((r) => r.json());
          if (!d.success) throw new Error(d.error || 'Could not delete the project');
          toast.success('Project deleted');
          window.location.assign('/project-management');
        } catch (err) { toast.error(err.message); }
      },
    });
  };

  const patchTask = async (task, patch) => {
    try {
      const d = await post(`/projects/tasks/${task.id}`, patch, 'PUT');
      setTaskPanel((p) => (p && p.id === task.id ? { ...p, ...d.task } : p));
      load();
    } catch (e) { toast.error(e.message); }
  };

  // ── dates: draft + explicit Save (so it's obvious what gets stored) ──
  useEffect(() => {
    if (!taskPanel) return;
    setDateDraft({
      start: taskPanel.start_date ? String(taskPanel.start_date).slice(0, 10) : '',
      end: taskPanel.end_date ? String(taskPanel.end_date).slice(0, 10) : ''
    });
  }, [taskPanel?.id, taskPanel?.start_date, taskPanel?.end_date]);

  const savedStart = taskPanel?.start_date ? String(taskPanel.start_date).slice(0, 10) : '';
  const savedEnd = taskPanel?.end_date ? String(taskPanel.end_date).slice(0, 10) : '';
  const datesDirty = !!taskPanel && (dateDraft.start !== savedStart || dateDraft.end !== savedEnd);

  const saveDates = async () => {
    if (!taskPanel || !datesDirty) return;
    await patchTask(taskPanel, {
      start_date: dateDraft.start || null,
      end_date: dateDraft.end || null
    });
    toast.success(
      dateDraft.start && !dateDraft.end
        ? `◆ Milestone saved — by ${fmtDate(dateDraft.start)}`
        : `📅 Dates saved — ${fmtDate(dateDraft.start)} → ${fmtDate(dateDraft.end)}`
    );
  };

  // ── company allocations on tasks (Phase 4) ──
  const assignCompany = async (task, value) => {
    if (!value) return;
    const [ctype, cid] = value.split('|');
    try {
      await post(`/projects/tasks/${task.id}/assignees`, {
        company_type: ctype,
        company_id: cid || null,
        name: ctype === 'op' ? 'One Purpose Logistics' : undefined,
        role_label: ctype === 'op' ? 'One Purpose Logistics' : 'Company'
      });
      load();
    } catch (e) { toast.error(e.message); }
  };

  // Company list for that dropdown (clients + subcontractors)
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const d = await apiFetch(`/projects/${id}/member-options`).then((r) => r.json());
        if (!dead && d.success) setCompanyOptions(d.companies || []);
      } catch { /* picker just stays empty */ }
    })();
    return () => { dead = true; };
  }, [id]);

  // Drag & drop in the Gantt: move a task to another phase / re-order it
  const moveTask = async (task, target) => {
    try {
      const d = await post(`/projects/tasks/${task.id}/move`, target, 'PUT');
      if (!d.success) throw new Error(d.error || 'Could not move the task');
      load();
    } catch (e) { toast.error(e.message || 'Could not move the task'); }
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!taskPanel) return;
    if (!itemForm.title || !itemForm.title.trim()) { toast.error('Give the requirement a name'); return; }
    try {
      await post(`/projects/tasks/${taskPanel.id}/items`, itemForm);
      setShowItemModal(false);
      setItemForm({ type: 'checklist', title: '', description: '', required: false, due_date: '' });
      load();
    } catch (err) { toast.error(err.message); }
  };

  const tickItem = async (item, status, note = '') => {
    try { await post(`/projects/items/${item.id}/checks`, { status, note }); load(); }
    catch (e) { toast.error(e.message); }
  };

  const addPerson = async (e) => {
    e.preventDefault();
    if (!taskPanel) return;
    try {
      await post(`/projects/tasks/${taskPanel.id}/assignees`, {
        name: personForm.name || undefined,
        email: personForm.email || undefined,
        role_label: personForm.role_label || undefined,
        is_external: true
      });
      setShowPersonModal(false);
      setPersonForm({ name: '', email: '', role_label: '' });
      load();
    } catch (err) { toast.error(err.message); }
  };

  const assignTmsUser = async (task, userId) => {
    const u = users.find((x) => x.id === userId);
    if (!u) return;
    try {
      await post(`/projects/tasks/${task.id}/assignees`, { user_id: u.id, name: u.full_name, email: u.email, is_external: false });
      load();
    } catch (e) { toast.error(e.message); }
  };

  const uploadFile = async (file, { taskId, itemId, caption }) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { toast.error('File too large (max 200MB)'); return; }
    setUploadingFor(itemId || taskId || 'task');
    try {
      const fd = new FormData();
      fd.append('image', file);
      const upRes = await fetch(`${API_URL}/uploads`, { method: 'POST', headers: authHeaders(), body: fd });
      const upData = await upRes.json();
      if (!upData.success) throw new Error(upData.error || 'Upload failed');
      await post(`/projects/${id}/files`, {
        file_url: upData.url,
        file_name: file.name,
        file_type: file.type.includes('pdf') ? 'pdf' : (file.type.startsWith('image/') ? 'image' : 'other'),
        file_size: file.size,
        task_id: taskId || null,
        item_id: itemId || null,
        caption: caption || file.name
      });
      toast.success('📎 Uploaded');
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploadingFor(null);
    }
  };

  const openLinkModal = (type) => {
    setLinkType(type); setLinkQuery(''); setShowLinkModal(true); searchLinks(type, '');
  };

  const searchLinks = async (type, q) => {
    try {
      const res = await apiFetch(`/projects/lookups/${type}?q=${encodeURIComponent(q || '')}`);
      const d = await res.json();
      if (d.success) setLinkOptions(d.items || []);
    } catch { setLinkOptions([]); }
  };

  const addLink = async (opt) => {
    try {
      await post(`/projects/${id}/links`, { entity_type: linkType, entity_id: opt.id, entity_label: opt.label });
      setShowLinkModal(false); load();
    } catch (e) { toast.error(e.message); }
  };

  const resetMemberForm = () => setMemberForm({
    member_type: 'company', company_type: '', company_id: '', user_id: '',
    name: '', email: '', role_label: '', member_role: 'editor',
    access_type: 'permanent', level: 'full', expires_at: '', projects_only: false
  });

  // Load the picker data (clients / subbies / their people / staff) then open
  const openMemberModal = async () => {
    setInviteLink(null);
    setMemberMode('company');
    resetMemberForm();
    setShowMemberModal(true);
    try {
      const d = await apiFetch(`/projects/${id}/member-options`).then((r) => r.json());
      if (d.success) setMemberOptions({ companies: d.companies || [], people: d.people || [] });
    } catch { setMemberOptions({ companies: [], people: [] }); }
  };

  const addMember = async (e) => {
    e.preventDefault();
    const f = memberForm;
    let payload;
    try {
      if (memberMode === 'company') {
        if (!f.company_id) { toast.error('Pick a company'); return; }
        const [ctype, cid] = f.company_id.split('|');
        payload = {
          member_type: 'company', company_type: ctype, company_id: cid || null,
          member_role: f.member_role, access_type: 'permanent'
        };
      } else if (memberMode === 'person') {
        if (!f.user_id) { toast.error('Pick a person'); return; }
        const [uid, ctype, cid] = f.user_id.split('|');
        const person = memberOptions.people.concat(...memberOptions.companies.map((c) => c.people || []))
          .find((p) => p.user_id === uid);
        payload = {
          member_type: 'person', user_id: uid,
          company_type: ctype && ctype !== 'op' ? ctype : null,
          company_id: cid || null,
          name: person?.label || null, email: person?.email || null,
          role_label: person?.role_label || null,
          member_role: f.member_role, access_type: 'permanent'
        };
      } else {
        if (!f.email) { toast.error('Email is required to invite someone'); return; }
        // NOTE: in this mode company_type / company_id are stored as SEPARATE
        // fields by the select above — do NOT re-split company_id (that used to
        // put the company UUID into company_type and lose the company link).
        payload = {
          member_type: 'person', name: f.name || null, email: f.email,
          company_type: f.company_type || null,
          company_id: f.company_id || null,
          role_label: f.role_label || null,
          member_role: f.member_role, access_type: 'permanent',
          // PHASE 5 — limit a new/external login to the project area
          portal_scope: f.projects_only ? 'projects_only' : 'full'
        };
      }

      // Access window applies to every mode (temporary access is time-boxed)
      payload.access_type = memberForm.access_type === 'temporary' ? 'temporary' : 'permanent';
      payload.expires_at = memberForm.access_type === 'temporary' ? (memberForm.expires_at || null) : null;

      const d = await post(`/projects/${id}/members`, payload);
      if (d.invited && d.invited.invite_link && !d.invited.email_sent) {
        // Email couldn't be sent — keep the modal open with the link to share
        setInviteLink(d.invited.invite_link);
        toast.info('Access added. Email not sent — copy the invite link below.');
      } else {
        toast.success(d.invited?.email_sent ? 'Access added — invitation emailed' : 'Access added');
        setShowMemberModal(false);
        resetMemberForm();
      }
      load();
    } catch (err) { toast.error(err.message); }
  };

  if (loading) {
    return (
      <AdminLayout activeTab="projects" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center py-24"><FaSpinner className="animate-spin text-3xl text-green" /></div>
      </AdminLayout>
    );
  }

  if (!project) {
    return (
      <AdminLayout activeTab="projects" onLogout={onLogout} user={user}>
        <div className="text-center py-24 text-gray-400">Project not found.</div>
      </AdminLayout>
    );
  }

  const canSeeCommercial = isStaff || user.role === 'client';
  const TABS = [
    { k: 'schedule', label: 'Schedule', icon: FaCalendarAlt },
    { k: 'files', label: 'Project Files', icon: FaFileAlt },
    ...(canSeeCommercial ? [{ k: 'financials', label: 'Quotes & Invoices', icon: FaLink }] : []),
    { k: 'loads', label: 'Loads', icon: FaTruck },
    { k: 'people', label: 'People & Access', icon: FaUsers },
    { k: 'activity', label: 'Activity', icon: FaHistory }
  ];

  return (
    <AdminLayout activeTab="projects" onLogout={onLogout} user={user}>
      {/* Page scrolls vertically; only the Gantt's timeline scrolls sideways */}
      <div className="w-full">
        <Link to="/project-management" className="mb-4 shrink-0 inline-flex items-center gap-2 text-gray-500 hover:text-navy">
          <FaArrowLeft /> All projects
        </Link>

        <div className="bg-white rounded-2xl border p-5 mb-4 shrink-0" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold text-gray-400">{project.project_number}</p>
              <h1 className="text-2xl font-bold text-navy">{project.name}</h1>
              <p className="text-sm text-gray-500">
                {project.client?.company_name || project.client?.trading_name || 'No client linked'}
                {project.origin || project.destination
                  ? ` · ${[project.origin, project.destination].filter(Boolean).join(' → ')}`
                  : (project.location ? ` · ${project.location}` : '')}
              </p>
            </div>
            <div className="text-right">
              {isStaff ? (
                <select
                  value={project.status}
                  onChange={async (e) => { await post(`/projects/${id}`, { status: e.target.value }, 'PUT'); load(); }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green"
                >
                  <option value="planning">Planning</option>
                  <option value="in_progress">In Progress</option>
                  <option value="on_hold">On Hold</option>
                  <option value="complete">Complete</option>
                </select>
              ) : (
                <span className="text-sm px-3 py-1 rounded-full bg-gray-100 text-gray-600">{project.status}</span>
              )}
              <p className="text-xs text-gray-500 mt-2">{fmtDate(project.start_date)} → {fmtDate(project.end_date)}</p>
              {canManageProject && (
                <div className="flex items-center justify-end gap-2 mt-3">
                  <button onClick={openEditProject} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-navy hover:bg-gray-50">Edit project</button>
                  <button onClick={deleteProject} className="px-3 py-1.5 border border-red-200 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50">Delete project</button>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
            {[
              { label: 'Overall', value: `${stats.pct}%`, color: '#0A1929' },
              { label: 'Tasks done', value: `${stats.done}/${stats.total}`, color: '#00A86B' },
              { label: 'In progress', value: stats.in_progress, color: '#2563EB' },
              { label: 'Overdue', value: stats.overdue, color: '#EF4444' },
              { label: 'Uploads pending', value: stats.pendingUploads, color: '#F59E0B' }
            ].map((c) => (
              <div key={c.label} className="rounded-xl border p-3" style={{ borderColor: '#E8ECEF' }}>
                <p className="text-[11px] uppercase tracking-wide text-gray-500">{c.label}</p>
                <p className="text-xl font-bold" style={{ color: c.color }}>{c.value}</p>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-green" style={{ width: `${stats.pct}%` }} />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4 shrink-0">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-colors ${tab === t.k ? 'bg-navy text-white' : 'bg-white text-gray-600 border hover:bg-gray-50'}`}
                style={tab === t.k ? {} : { borderColor: '#E8ECEF' }}
              >
                <Icon size={13} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab pane */}
        <div>
        {tab === 'schedule' && (
          <div>
            <div className="flex flex-wrap gap-2 mb-3 shrink-0">
              <button onClick={() => setShowTaskModal(true)} className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-navy flex items-center gap-2">
                <FaPlus size={12} /> Add task
              </button>
              <button onClick={() => setShowPhaseModal(true)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2">
                <FaPlus size={12} /> Add phase
              </button>
              <button
                onClick={() => { setShiftPreview(null); setShowRescheduleModal(true); }}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2"
                title="Move the project (or one phase) and everything under it"
              >
                📅 Reschedule
              </button>
            </div>
            <ProjectGantt
              phases={phases}
              tasks={tasks}
              project={project}
              onOpenTask={(t) => setTaskPanel(t)}
                      onMovePhase={movePhase}
                      onDeletePhase={deletePhase}
              onReschedule={reschedule}
              onAddTask={(phaseId) => { setTaskForm((f) => ({ ...f, phase_id: phaseId || '' })); setShowTaskModal(true); }}
              canEditTask={canEditTask}
              onMoveTask={moveTask}
            />
          </div>
        )}

        {tab === 'files' && (
          <div className="bg-white rounded-2xl border p-5" style={{ borderColor: '#E8ECEF' }}>
            <h2 className="font-bold text-navy mb-3">Project Files ({data.files?.length || 0})</h2>
            {(data.files || []).length === 0 ? (
              <p className="text-sm text-gray-400">No files yet — upload from a task, or add one here.</p>
            ) : (
              <div className="space-y-2">
                {(data.files || []).map((f) => {
                  const task = tasks.find((t) => t.id === f.task_id);
                  return (
                    <div key={f.id} className="flex items-center justify-between gap-3 border rounded-xl p-3" style={{ borderColor: '#E8ECEF' }}>
                      <div className="min-w-0">
                        <a href={f.file_url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-navy hover:text-green underline truncate block">
                          {f.caption || f.file_name || 'file'}
                        </a>
                        <p className="text-xs text-gray-400">
                          {f.uploaded_by_name || 'Unknown'} · {new Date(f.created_at).toLocaleString()}
                          {task ? ` · ${task.name}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{f.file_type || 'file'}</span>
                        {(isStaff || f.uploaded_by === user.id) && (
                          <button
                            onClick={() => deleteFile(f)}
                            className="text-xs text-red-500 hover:text-red-700"
                          >
                            <FaTrash size={11} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="mt-4">
              <label className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 cursor-pointer">
                {uploadingFor === 'task' ? <FaSpinner className="animate-spin" /> : <FaUpload size={12} />} Upload a project file
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => uploadFile(e.target.files[0], { caption: e.target.files[0]?.name })}
                />
              </label>
            </div>
          </div>
        )}

        {(tab === 'financials' || tab === 'loads') && (
          <div className="bg-white rounded-2xl border p-5" style={{ borderColor: '#E8ECEF' }}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-navy">
                {tab === 'financials' ? 'Quotations & Invoices' : 'Loads'}{' '}
                ({data.links.filter((l) => (tab === 'financials' ? l.entity_type !== 'load' : l.entity_type === 'load')).length})
              </h2>
              <div className="flex gap-2">
                {tab === 'financials' ? (
                  <>
                    <button onClick={() => openLinkModal('quote')} className="px-3 py-1.5 bg-green text-white rounded-lg text-xs font-medium hover:bg-navy flex items-center gap-1">
                      <FaPlus size={10} /> Link quotation
                    </button>
                    <button onClick={() => openLinkModal('invoice')} className="px-3 py-1.5 bg-navy text-white rounded-lg text-xs font-medium hover:bg-gray-800 flex items-center gap-1">
                      <FaPlus size={10} /> Link invoice
                    </button>
                  </>
                ) : (
                  <button onClick={() => openLinkModal('load')} className="px-3 py-1.5 bg-green text-white rounded-lg text-xs font-medium hover:bg-navy flex items-center gap-1">
                    <FaPlus size={10} /> Link load
                  </button>
                )}
              </div>
            </div>
            {data.links.filter((l) => (tab === 'financials' ? l.entity_type !== 'load' : l.entity_type === 'load')).length === 0 ? (
              <p className="text-sm text-gray-400">Nothing linked yet.</p>
            ) : (
              <div className="space-y-2">
                {data.links
                  .filter((l) => (tab === 'financials' ? l.entity_type !== 'load' : l.entity_type === 'load'))
                  .map((l) => (
                    <div key={l.id} className="flex items-center justify-between border rounded-xl p-3" style={{ borderColor: '#E8ECEF' }}>
                      <div>
                        <p className="text-sm font-medium text-navy">{l.entity_label || l.entity_id}</p>
                        <p className="text-xs text-gray-400 capitalize">{l.entity_type}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        {l.entity_type === 'load' && <a href="/active-jobs" className="text-xs text-green font-medium hover:underline">Track load →</a>}
                        {l.entity_type === 'quote' && <a href={`/quotes/${l.entity_id}`} className="text-xs text-green font-medium hover:underline">Open →</a>}
                        {l.entity_type === 'invoice' && <a href="/invoices" className="text-xs text-green font-medium hover:underline">Open →</a>}
                        {(isStaff || l.created_by === user.id) && (
                          <button onClick={async () => { await apiFetch(`/projects/links/${l.id}`, { method: 'DELETE' }); load(); }} className="text-xs text-red-500 hover:text-red-700"><FaTrash size={11} /></button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}


        {tab === 'people' && (
          <div className="bg-white rounded-2xl border p-5" style={{ borderColor: '#E8ECEF' }}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-navy">People & Access ({data.members.length})</h2>
              {isStaff && (
                <button onClick={openMemberModal} className="px-3 py-1.5 bg-green text-white rounded-lg text-xs font-medium hover:bg-navy flex items-center gap-1">
                  <FaUserPlus size={10} /> Add access
                </button>
              )}
            </div>
            <div className="space-y-2">
              {data.members.length === 0 && <p className="text-sm text-gray-400">No one added yet. Client &amp; subbie people only see projects they are granted.</p>}
              {data.members.map((m) => {
                const comp = memberOptions.companies.find((c) => c.id === m.company_id);
                const roleLabel = { viewer: 'Viewer', editor: 'Editor', manager: 'Manager' }[m.member_role || 'editor'];
                const roleColour = { viewer: '#64748B', editor: '#2563EB', manager: '#00A86B' }[m.member_role || 'editor'];
                const isCompanyRow = m.member_type === 'company';
                return (
                  <div key={m.id} className="flex items-center justify-between border rounded-xl p-3" style={{ borderColor: '#E8ECEF' }}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-navy truncate">
                        {isCompanyRow
                          ? `${comp?.name || 'Company'} (everyone)`
                          : (m.name || m.email || 'Person')}
                        <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${roleColour}1A`, color: roleColour }}>
                          {roleLabel}
                        </span>
                        {isCompanyRow && <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">Company</span>}
                      </p>
                      <p className="text-xs text-gray-400">
                        {comp && !isCompanyRow ? `${comp.name} · ` : ''}
                        {m.company_role ? `${m.company_role} · ` : ''}
                        {m.access_type === 'temporary'
                          ? `Temporary${m.expires_at ? ` until ${fmtDate(m.expires_at)}` : ''}`
                          : 'Permanent'}
                      </p>
                    </div>
                    {(isStaff || myMemberRow(m) || data.members.some((x) => myMemberRow(x) && x.member_role === 'manager')) && (
                      <button
                        onClick={async () => { await apiFetch(`/projects/members/${m.id}`, { method: 'DELETE' }); load(); }}
                        className="text-xs text-red-500 hover:text-red-700 shrink-0 ml-2"
                        title={myMemberRow(m) ? 'Remove my access' : 'Remove access'}
                      ><FaTrash size={11} /></button>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-5 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
              <h3 className="text-sm font-semibold text-navy mb-2">Allocated to tasks</h3>
              <div className="flex flex-wrap gap-2">
                {tasks.flatMap((t) => (t.assignees || []).map((a) => ({ ...a, task: t.name }))).length === 0 && (
                  <p className="text-sm text-gray-400">No task allocations yet.</p>
                )}
                {tasks.flatMap((t) => (t.assignees || []).map((a) => ({ ...a, task: t.name }))).map((a) => (
                  <span key={a.id} className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                    {a.name || a.email} · {a.task}{a.role_label ? ` (${a.role_label})` : ''}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === 'activity' && (
          <div className="bg-white rounded-2xl border p-5" style={{ borderColor: '#E8ECEF' }}>
            <h2 className="font-bold text-navy mb-3">Activity</h2>
            <div className="space-y-2">
              {(data.activity || []).length === 0 && <p className="text-sm text-gray-400">Nothing yet.</p>}
              {(data.activity || []).map((a) => (
                <div key={a.id} className="flex items-start gap-3 text-sm border-b pb-2" style={{ borderColor: '#F1F5F9' }}>
                  <span className="text-xs text-gray-400 w-36 shrink-0">{new Date(a.created_at).toLocaleString()}</span>
                  <span className="text-gray-700">
                    <strong className="capitalize">{String(a.action || '').replace(/_/g, ' ')}</strong>
                    {a.detail ? ` — ${a.detail}` : ''}
                  </span>
                  <span className="ml-auto text-xs text-gray-400 shrink-0">{a.user_name || ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        </div>

        {taskPanel && (
          <div className="fixed inset-0 z-50 flex justify-end">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setTaskPanel(null)}></div>
            <div className="relative bg-white w-full max-w-lg h-full overflow-y-auto shadow-2xl">
              <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-start justify-between" style={{ borderColor: '#E8ECEF' }}>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-gray-400">Task</p>
                  <h2 className="text-lg font-bold text-navy">{taskPanel.name}</h2>
                </div>
                <button onClick={() => setTaskPanel(null)} className="text-gray-500 hover:text-navy"><FaTimes /></button>
              </div>

              <div className="p-5 space-y-5">
                <div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Start</label>
                      <input
                        type="date"
                        disabled={!canEditTask(taskPanel)}
                        value={dateDraft.start}
                        onChange={(e) => setDateDraft((d) => ({ ...d, start: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveDates(); }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50 focus:outline-none focus:border-green"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">End</label>
                      <input
                        type="date"
                        disabled={!canEditTask(taskPanel)}
                        value={dateDraft.end}
                        onChange={(e) => setDateDraft((d) => ({ ...d, end: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveDates(); }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50 focus:outline-none focus:border-green"
                      />
                    </div>
                  </div>
                  {canEditTask(taskPanel) && (
                    <div className="flex items-center gap-3 mt-2">
                      <button
                        type="button"
                        onClick={saveDates}
                        disabled={!datesDirty}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors ${datesDirty ? 'bg-green text-white hover:bg-navy' : 'bg-gray-100 text-gray-400'}`}
                      >
                        <FaCheck size={10} /> {datesDirty ? 'Save dates' : 'Dates saved'}
                      </button>
                      {datesDirty && <span className="text-[11px] text-amber-600">Unsaved change — press Save dates (or Enter)</span>}
                    </div>
                  )}
                  <p className="text-[11px] text-gray-400 mt-2">
                    Leave <strong>End</strong> blank to make this a <strong>◆ milestone</strong> — a "by this date" item (permits, approvals). Fill both for a duration bar.
                  </p>
                </div>
                <div className="space-y-4">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                    <select
                      disabled={!canEditTask(taskPanel)}
                      value={taskPanel.status || 'not_started'}
                      onChange={(e) => patchTask(taskPanel, { status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50 focus:outline-none focus:border-green"
                    >
                      {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Progress: {taskPanel.progress || 0}%</label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="5"
                      disabled={!canEditTask(taskPanel)}
                      value={taskPanel.progress || 0}
                      onChange={(e) => setTaskPanel((p) => ({ ...p, progress: Number(e.target.value) }))}
                      onMouseUp={(e) => patchTask(taskPanel, { progress: Number(e.target.value) })}
                      className="w-full"
                    />
                  </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-navy">Priority / risk</h3>
                    <span className="text-[11px] text-gray-400">Urgent tasks get a red outline on the bar</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { k: 'urgent', label: 'Urgent', color: '#EF4444' },
                      { k: 'high', label: 'High', color: '#F97316' },
                      { k: 'moderate', label: 'Moderate', color: '#EAB308' },
                      { k: 'low', label: 'Low', color: '#3B82F6' }
                    ].map((p) => {
                      const active = (taskPanel.priority || 'moderate') === p.k;
                      return (
                        <button
                          key={p.k}
                          onClick={() => patchTask(taskPanel, { priority: p.k })}
                          className="text-xs px-3 py-1.5 rounded-full border font-medium flex items-center gap-1.5 transition-colors"
                          style={active ? { backgroundColor: p.color, borderColor: p.color, color: '#fff' } : { borderColor: '#E8ECEF', color: '#4A5568' }}
                        >
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: active ? '#fff' : p.color }} />
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-navy">Allocated to</h3>
                    <div className="flex gap-2">
                      {isStaff && (
                        <select
                          defaultValue=""
                          onChange={(e) => { assignTmsUser(taskPanel, e.target.value); e.target.value = ''; }}
                          className="px-2 py-1 border border-gray-300 rounded-lg text-xs focus:outline-none focus:border-green"
                        >
                          <option value="">+ TMS user…</option>
                          {users.map((u) => <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>)}
                        </select>
                      )}
                      {isStaff && (
                        <select
                          defaultValue=""
                          onChange={(e) => { assignCompany(taskPanel, e.target.value); e.target.value = ''; }}
                          className="px-2 py-1 border border-gray-300 rounded-lg text-xs focus:outline-none focus:border-green"
                          title="Allocate this task to a whole company"
                        >
                          <option value="">+ Company…</option>
                          <optgroup label="One Purpose Logistics">
                            <option value="op|">One Purpose Logistics</option>
                          </optgroup>
                          <optgroup label="Clients">
                            {companyOptions.filter((c) => c.type === 'client').map((c) => (
                              <option key={c.id} value={`client|${c.id}`}>{c.name}</option>
                            ))}
                          </optgroup>
                          <optgroup label="Subcontractors">
                            {companyOptions.filter((c) => c.type === 'subcontractor').map((c) => (
                              <option key={c.id} value={`subcontractor|${c.id}`}>{c.name}</option>
                            ))}
                          </optgroup>
                        </select>
                      )}
                      <button onClick={() => setShowPersonModal(true)} className="px-2 py-1 border border-gray-200 rounded-lg text-xs hover:bg-gray-50">+ External</button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(taskPanel.assignees || []).length === 0 && <p className="text-xs text-gray-400">Nobody allocated yet.</p>}
                    {(taskPanel.assignees || []).map((a) => (
                      <span key={a.id} className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 flex items-center gap-2">
                        {a.company_type
                          ? `🏢 ${a.name || 'Company'}`
                          : <>{a.name || a.email}{a.role_label ? ` · ${a.role_label}` : ''}{a.is_external ? ' (ext)' : ''}</>}
                        {(isStaff || a.created_by === user.id) && (
                          <button onClick={async () => { await apiFetch(`/projects/assignees/${a.id}`, { method: 'DELETE' }); load(); }} className="text-red-400 hover:text-red-600"><FaTimes size={9} /></button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-navy">Requirements</h3>
                    <button onClick={() => setShowItemModal(true)} className="px-2 py-1 bg-green text-white rounded-lg text-xs font-medium hover:bg-navy flex items-center gap-1">
                      <FaPlus size={9} /> Add
                    </button>
                  </div>
                  <div className="space-y-2">
                    {(taskPanel.items || []).length === 0 && (
                      <p className="text-xs text-gray-400">Nothing required yet — add a checklist, an upload (certificate/photo) or a feedback item.</p>
                    )}
                    {(taskPanel.items || []).map((it) => {
                      const checks = it.checks || [];
                      const done = checks.some((c) => c.status === 'done');
                      const flagged = checks.some((c) => c.status === 'flagged');
                      const files = (data.files || []).filter((f) => f.item_id === it.id);
                      return (
                        <div key={it.id} className="border rounded-xl p-3" style={{ borderColor: '#E8ECEF' }}>
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-medium text-navy flex flex-wrap items-center gap-2">
                                {it.type === 'upload' ? '📎' : it.type === 'feedback' ? '💬' : '✅'} {it.title}
                                {it.required && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-600">required</span>}
                                {done && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-700">done</span>}
                                {flagged && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">flagged</span>}
                              </p>
                              {it.due_date && <p className="text-xs text-gray-400">Due {fmtDate(it.due_date)}</p>}
                            </div>
                            {(isStaff || it.created_by === user.id) && (
                              <button onClick={async () => { await apiFetch(`/projects/items/${it.id}`, { method: 'DELETE' }); load(); }} className="text-xs text-red-400 hover:text-red-600"><FaTrash size={10} /></button>
                            )}
                          </div>

                          {it.type === 'upload' && (
                            <div className="mt-2">
                              {files.map((f) => (
                                <div key={f.id} className="flex items-center gap-2 text-xs">
                                  <a href={f.file_url} target="_blank" rel="noopener noreferrer" className="text-green font-medium hover:underline truncate">{f.file_name || 'file'}</a>
                                  <span className="text-gray-400">· {f.uploaded_by_name}</span>
                                  {(isStaff || f.uploaded_by === user.id) && (
                                    <button onClick={() => deleteFile(f)} className="text-red-400 hover:text-red-600 ml-auto"><FaTimes size={9} /></button>
                                  )}
                                </div>
                              ))}
                              <label className="mt-1 inline-flex items-center gap-2 px-3 py-1.5 border border-dashed border-gray-300 rounded-lg text-xs cursor-pointer hover:border-green">
                                {uploadingFor === it.id ? <FaSpinner className="animate-spin" /> : <FaUpload size={10} />} {files.length ? 'Add another file' : 'Upload'}
                                <input type="file" className="hidden" onChange={(e) => uploadFile(e.target.files[0], { taskId: taskPanel.id, itemId: it.id, caption: it.title })} />
                              </label>
                            </div>
                          )}

                          {(it.type === 'checklist' || it.type === 'feedback') && (
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <button onClick={() => tickItem(it, 'done')} className="px-2 py-1 bg-green text-white rounded-lg text-xs font-medium hover:bg-navy flex items-center gap-1">
                                <FaCheck size={9} /> {it.type === 'feedback' ? 'Acknowledge' : 'Tick'}
                              </button>
                              <button onClick={() => tickItem(it, 'flagged', 'Flagged')} className="px-2 py-1 border border-amber-300 text-amber-700 rounded-lg text-xs hover:bg-amber-50">Flag</button>
                              <span className="text-xs text-gray-400">
                                {checks[0] ? `${checks[0].user_name || ''} · ${new Date(checks[0].created_at).toLocaleDateString('en-ZA')}` : 'not done'}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-navy">Task photos &amp; files</h3>
                    <label className="inline-flex items-center gap-2 px-2 py-1 border border-gray-200 rounded-lg text-xs cursor-pointer hover:bg-gray-50">
                      {uploadingFor === taskPanel.id ? <FaSpinner className="animate-spin" /> : <FaUpload size={10} />} Upload
                      <input type="file" className="hidden" onChange={(e) => uploadFile(e.target.files[0], { taskId: taskPanel.id, caption: taskPanel.name })} />
                    </label>
                  </div>
                  <div className="space-y-1">
                    {(data.files || []).filter((f) => f.task_id === taskPanel.id && !f.item_id).length === 0 && (
                      <p className="text-xs text-gray-400">No files on this task yet.</p>
                    )}
                    {(data.files || []).filter((f) => f.task_id === taskPanel.id && !f.item_id).map((f) => (
                      <div key={f.id} className="flex items-center gap-2 text-xs">
                        <a href={f.file_url} target="_blank" rel="noopener noreferrer" className="text-green font-medium hover:underline truncate">{f.file_name || 'file'}</a>
                        <span className="text-gray-400">· {f.uploaded_by_name}</span>
                        {(isStaff || f.uploaded_by === user.id) && (
                          <button onClick={() => deleteFile(f)} className="text-red-400 hover:text-red-600 ml-auto"><FaTimes size={9} /></button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {canEditTask(taskPanel) && (
                  <div className="pt-4 border-t flex justify-end" style={{ borderColor: '#E8ECEF' }}>
                    <button onClick={() => deleteTask(taskPanel)} className="px-3 py-1.5 border border-red-300 text-red-600 rounded-lg text-xs hover:bg-red-50 flex items-center gap-1">
                      <FaTrash size={10} /> Delete task
                    </button>
                  </div>
                )}

              </div>
            </div>
          </div>
        )}
      </div>
      {showRescheduleModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setShowRescheduleModal(false); setShiftPreview(null); }}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-navy mb-1">📅 Reschedule</h2>
            <p className="text-xs text-gray-500 mb-4">
              Move the whole project, or just one phase, with everything under it. Gaps are preserved and
              completed tasks keep their real dates. Preview first — nothing is saved until you apply.
            </p>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">What to move</label>
                  <select
                    value={shiftForm.scope}
                    onChange={(e) => { setShiftForm({ ...shiftForm, scope: e.target.value }); setShiftPreview(null); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                  >
                    <option value="project">Whole project</option>
                    <option value="phase">One phase</option>
                  </select>
                </div>
                {shiftForm.scope === 'phase' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phase</label>
                    <select
                      value={shiftForm.phase_id}
                      onChange={(e) => { setShiftForm({ ...shiftForm, phase_id: e.target.value }); setShiftPreview(null); }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    >
                      <option value="">— choose a phase —</option>
                      {(phases || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">How</label>
                  <select
                    value={shiftForm.mode}
                    onChange={(e) => { setShiftForm({ ...shiftForm, mode: e.target.value }); setShiftPreview(null); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                  >
                    <option value="days">Shift by days</option>
                    <option value="start">Start on a date</option>
                  </select>
                </div>
                {shiftForm.mode === 'days' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Days (+ or −)</label>
                    <input
                      type="number"
                      value={shiftForm.days}
                      onChange={(e) => { setShiftForm({ ...shiftForm, days: e.target.value }); setShiftPreview(null); }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New start date</label>
                    <input
                      type="date"
                      value={shiftForm.new_start}
                      onChange={(e) => { setShiftForm({ ...shiftForm, new_start: e.target.value }); setShiftPreview(null); }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    />
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={shiftForm.skip_completed}
                  onChange={(e) => { setShiftForm({ ...shiftForm, skip_completed: e.target.checked }); setShiftPreview(null); }}
                  className="w-4 h-4 accent-green"
                />
                Leave completed tasks where they are (recommended)
              </label>

              {shiftPreview && (
                <div className="border rounded-xl p-3 max-h-56 overflow-y-auto" style={{ borderColor: '#E8ECEF' }}>
                  <p className="text-xs font-semibold text-navy mb-2">
                    {shiftPreview.delta_days > 0 ? '+' : ''}{shiftPreview.delta_days} day(s)
                    {shiftPreview.from_start ? ` • ${fmtDate(shiftPreview.from_start)} to ${fmtDate(shiftPreview.to_start)}` : ''}
                  </p>
                  {shiftPreview.skipped_completed_tasks > 0 && (
                    <p className="text-[11px] text-gray-500 mb-2">{shiftPreview.skipped_completed_tasks} completed task(s) will keep their dates.</p>
                  )}
                  <div className="space-y-1">
                    {(shiftPreview.changes || []).slice(0, 60).map((c, i) => (
                      <div key={c.kind + '-' + c.id + '-' + i} className="flex items-center justify-between gap-2 text-[11px]">
                        <span className="text-gray-600 truncate">
                          <span className="uppercase text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 mr-1">{c.kind}</span>
                          {c.name}
                        </span>
                        <span className="text-gray-500 shrink-0">
                          {c.from.start_date ? fmtDate(c.from.start_date) : '—'} → <span className="text-navy font-medium">{c.to.start_date ? fmtDate(c.to.start_date) : '—'}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                  {(shiftPreview.changes || []).length === 0 && (
                    <p className="text-xs text-gray-400">Nothing would change.</p>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-between items-center gap-3 mt-6 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
              <button
                onClick={() => runShift(true)}
                disabled={shiftBusy || (shiftForm.scope === 'phase' && !shiftForm.phase_id)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                Preview
              </button>
              <div className="flex gap-3">
                <button onClick={() => { setShowRescheduleModal(false); setShiftPreview(null); }} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button
                  onClick={() => runShift(false)}
                  disabled={shiftBusy || !shiftPreview || (shiftPreview.counts && (shiftPreview.counts.projects + shiftPreview.counts.phases + shiftPreview.counts.tasks) === 0)}
                  className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-navy disabled:opacity-50"
                >
                  {shiftBusy ? 'Working…' : 'Apply reschedule'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTaskModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowTaskModal(false)}></div>
          <form onSubmit={addTask} className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-navy mb-4">Add Task</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Task name *</label>
                <input value={taskForm.name} onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" placeholder="e.g. Route survey" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phase</label>
                <select value={taskForm.phase_id} onChange={(e) => setTaskForm({ ...taskForm, phase_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green">
                  <option value="">— none (General) —</option>
                  {phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start</label>
                  <input type="date" value={taskForm.start_date} onChange={(e) => setTaskForm({ ...taskForm, start_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End</label>
                  <input type="date" value={taskForm.end_date} onChange={(e) => setTaskForm({ ...taskForm, end_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={taskForm.status} onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green">
                  {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea rows="2" value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
              <button type="button" onClick={() => setShowTaskModal(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-navy flex items-center gap-2"><FaPlus size={12} /> Add task</button>
            </div>
          </form>
        </div>
      )}

      {showPhaseModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowPhaseModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-navy mb-4">Add Phase</h2>
            <input value={phaseName} onChange={(e) => setPhaseName(e.target.value)} placeholder="e.g. Transport, Rigging…" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" />
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowPhaseModal(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={addPhase} className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-navy">Add phase</button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowEditModal(false)}></div>
          <form onSubmit={saveProject} className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-navy mb-4">Edit project</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project name</label>
                <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start date</label>
                  <input type="date" value={editForm.start_date} onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End date</label>
                  <input type="date" value={editForm.end_date} onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" placeholder="e.g. Manchester" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea rows="3" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" />
              </div>
              <p className="text-xs text-gray-400">Changing the dates here moves the project window only — phases and tasks keep their own dates.</p>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">Cancel</button>
              <button type="submit" className="px-6 py-2 rounded-lg text-sm font-semibold text-white bg-green hover:bg-green-600">Save changes</button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmState}
        onClose={() => setConfirmState(null)}
        title={confirmState?.title || 'Confirm'}
        message={confirmState?.message || ''}
        confirmLabel={confirmState?.confirmLabel || 'Confirm'}
        danger={!!confirmState?.danger}
        onConfirm={() => { const fn = confirmState?.onConfirm; if (fn) fn(); }}
      />

      {showItemModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowItemModal(false)}></div>
          <form onSubmit={addItem} className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-navy mb-4">Add Requirement</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {ITEM_TYPES.map((t) => (
                    <button
                      key={t.k}
                      type="button"
                      onClick={() => setItemForm({ ...itemForm, type: t.k })}
                      className={`px-3 py-2 rounded-lg text-xs font-medium border ${itemForm.type === t.k ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                      style={itemForm.type === t.k ? {} : { borderColor: '#E8ECEF' }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">What is it called? *</label>
                <input value={itemForm.title} onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" placeholder="e.g. Abnormal load permit / Route survey report" />
              </div>
              <div className="grid grid-cols-2 gap-3 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due date</label>
                  <input type="date" value={itemForm.due_date} onChange={(e) => setItemForm({ ...itemForm, due_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
                  <input type="checkbox" checked={itemForm.required} onChange={(e) => setItemForm({ ...itemForm, required: e.target.checked })} /> Required
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea rows="2" value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" onClick={() => setShowItemModal(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-navy">Add</button>
            </div>
          </form>
        </div>
      )}

      {showPersonModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowPersonModal(false)}></div>
          <form onSubmit={addPerson} className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-navy mb-1">Allocate someone (external)</h2>
            <p className="text-xs text-gray-500 mb-4">For people not in the TMS — a rigging company, surveyor, or the client’s engineer.</p>
            <div className="space-y-3">
              <input value={personForm.name} onChange={(e) => setPersonForm({ ...personForm, name: e.target.value })} placeholder="Name *" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" />
              <input value={personForm.email} onChange={(e) => setPersonForm({ ...personForm, email: e.target.value })} placeholder="Email" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" />
              <input value={personForm.role_label} onChange={(e) => setPersonForm({ ...personForm, role_label: e.target.value })} placeholder="Role (Rigger, PM, Surveyor…)" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" />
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" onClick={() => setShowPersonModal(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-navy">Allocate</button>
            </div>
          </form>
        </div>
      )}

      {showLinkModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowLinkModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-navy mb-4 capitalize">Link {linkType}</h2>
            <input
              value={linkQuery}
              onChange={(e) => { setLinkQuery(e.target.value); searchLinks(linkType, e.target.value); }}
              placeholder={`Search ${linkType} number…`}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green mb-3"
            />
            <div className="space-y-2">
              {linkOptions.length === 0 && <p className="text-sm text-gray-400">Nothing found.</p>}
              {linkOptions.map((o) => (
                <button key={o.id} onClick={() => addLink(o)} className="w-full text-left border rounded-xl p-3 hover:border-green hover:bg-gray-50" style={{ borderColor: '#E8ECEF' }}>
                  <span className="text-sm font-medium text-navy">{o.label}</span>
                  {o.sub && <span className="text-xs text-gray-400 ml-2">{o.sub}</span>}
                </button>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => setShowLinkModal(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Close</button>
            </div>
          </div>
        </div>
      )}

      {showMemberModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowMemberModal(false)}></div>
          <form onSubmit={addMember} className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-navy mb-1">Add access</h2>
            <p className="text-xs text-gray-500 mb-4">
              <strong>Viewer</strong> = read-only · <strong>Editor</strong> = can add, and edit/delete what they added · <strong>Manager</strong> = also manages access.
              Nobody can delete what One Purpose created.
            </p>

            <div className="flex gap-2 mb-4">
              {[
                { k: 'company', label: 'Company' },
                { k: 'person', label: 'Existing person' },
                { k: 'invite', label: 'Invite new person' }
              ].map((m) => (
                <button
                  key={m.k}
                  type="button"
                  onClick={() => setMemberMode(m.k)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium ${memberMode === m.k ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >{m.label}</button>
              ))}
            </div>

            <div className="space-y-3">
              {memberMode === 'company' && (
                <select
                  value={memberForm.company_id ? `${memberForm.company_type}|${memberForm.company_id}` : ''}
                  onChange={(e) => {
                    const [t, cid] = e.target.value.split('|');
                    setMemberForm({ ...memberForm, company_type: t || '', company_id: cid || '' });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">Add a whole company — everyone in it gets access…</option>
                  <optgroup label="Clients">
                    {memberOptions.companies.filter((c) => c.type === 'client').map((c) => (
                      <option key={c.id} value={`client|${c.id}`}>{c.name}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Subcontractors">
                    {memberOptions.companies.filter((c) => c.type === 'subcontractor').map((c) => (
                      <option key={c.id} value={`subcontractor|${c.id}`}>{c.name}</option>
                    ))}
                  </optgroup>
                </select>
              )}

              {memberMode === 'person' && (
                <select
                  value={memberForm.user_id}
                  onChange={(e) => setMemberForm({ ...memberForm, user_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">Pick an existing person…</option>
                  <optgroup label="One Purpose Logistics">
                    {memberOptions.people.map((p) => (
                      <option key={p.user_id} value={`${p.user_id}|op|`}>{p.label} ({p.role})</option>
                    ))}
                  </optgroup>
                  {memberOptions.companies.filter((c) => (c.people || []).length).map((c) => (
                    <optgroup key={c.id} label={c.name}>
                      {c.people.map((p) => (
                        <option key={p.user_id} value={`${p.user_id}|${c.type}|${c.id}`}>{p.label}{p.role_label ? ` · ${p.role_label}` : ''}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              )}

              {memberMode === 'invite' && (
                <>
                  <input value={memberForm.name} onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })} placeholder="Full name" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  <input value={memberForm.email} onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })} placeholder="Email (they get an invitation)" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  <select
                    value={memberForm.company_id ? `${memberForm.company_type}|${memberForm.company_id}` : ''}
                    onChange={(e) => {
                      const [t, cid] = e.target.value.split('|');
                      setMemberForm({ ...memberForm, company_type: t || '', company_id: cid || '' });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">Which company are they from…</option>
                    <optgroup label="Clients">
                      {memberOptions.companies.filter((c) => c.type === 'client').map((c) => (
                        <option key={c.id} value={`client|${c.id}`}>{c.name}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Subcontractors">
                      {memberOptions.companies.filter((c) => c.type === 'subcontractor').map((c) => (
                        <option key={c.id} value={`subcontractor|${c.id}`}>{c.name}</option>
                      ))}
                    </optgroup>
                  </select>
                  <input value={memberForm.role_label} onChange={(e) => setMemberForm({ ...memberForm, role_label: e.target.value })} placeholder="Their role (MD, Procurement, Rigger…)" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  <label className="flex items-start gap-2 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={!!memberForm.projects_only}
                      onChange={(e) => setMemberForm({ ...memberForm, projects_only: e.target.checked })}
                    />
                    <span>
                      <strong>Projects-only login</strong> — they only see the project area (no quotes, invoices or reports).
                      Best for temporary / external people.
                    </span>
                  </label>
                </>
              )}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Project access level</label>
                <select
                  value={memberForm.member_role}
                  onChange={(e) => setMemberForm({ ...memberForm, member_role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="viewer">Viewer — read only</option>
                  <option value="editor">Editor — add, and edit/delete their own</option>
                  {isAdmin && <option value="manager">Manager — editor + manages access</option>}
                </select>
              </div>

              {/* Temporary / time-boxed access */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Access window</label>
                  <select
                    value={memberForm.access_type}
                    onChange={(e) => setMemberForm({ ...memberForm, access_type: e.target.value, ...(e.target.value === 'temporary' ? { projects_only: true } : {}) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="permanent">Permanent</option>
                    <option value="temporary">Temporary (times out)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Access until</label>
                  <input
                    type="date"
                    value={memberForm.expires_at}
                    disabled={memberForm.access_type !== 'temporary'}
                    onChange={(e) => setMemberForm({ ...memberForm, expires_at: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:text-gray-400"
                  />
                </div>
              </div>
            </div>

            {inviteLink && (
              <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3">
                <p className="text-xs font-semibold text-amber-800 mb-1">Invitation email could not be sent — share this link:</p>
                <textarea readOnly value={inviteLink} className="w-full text-[11px] p-2 rounded border border-amber-200 bg-white" rows={3} />
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button type="button" onClick={() => setShowMemberModal(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Close</button>
              <button type="submit" className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-navy">Add access</button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}

export default ProjectDetailPage;


